// app/frontend/components/ui/Inputs/ThemedTextarea.tsx
import React from 'react';
import { Textarea, TextareaProps } from '@mantine/core';
import '../../../styles/ThemedInputs.css';
import { COLORS, INPUT_LABEL_FONT_SIZE } from '../../../src/constants/theme';

interface ThemedTextareaProps extends Omit<TextareaProps, 'styles'> {
  label?: React.ReactNode;
  placeholder?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  minRows?: number;
  maxRows?: number;
  description?: string;
  error?: string;
}

/**
 * Two kinds of text box (#1830):
 * - `autosize`: grows as you type. For short fields (a description, a note).
 * - Fixed (no `autosize`): long writing. Starts at `minRows` lines and has a
 *   drag handle in the corner, so the person decides how much room they want.
 *   Pass `resize="none"` to turn the handle off.
 * The two don't mix: autosize resets the height on every keystroke, undoing a
 * drag. And Mantine only reads `minRows` when autosizing, so a fixed box gets
 * it as `rows` here; before, every fixed box opened at the browser default.
 */
const ThemedTextarea: React.FC<ThemedTextareaProps> = (props) => {
  const fixed = !props.autosize;
  return (
    <Textarea
      {...props}
      rows={props.rows ?? (fixed ? props.minRows : undefined)}
      resize={props.resize ?? (fixed ? 'vertical' : 'none')}
      className="themed-textarea"
      styles={{
        label: { 
          color: '#f2f2f2', 
          marginBottom: '4px', 
          fontWeight: 500,
          fontSize: INPUT_LABEL_FONT_SIZE,
        },
        description: {
          color: COLORS.textDimmed,
          fontSize: '0.75rem',
          marginTop: '3px',
          lineHeight: 1.4,
        },
        error: {
          color: '#ff6b6b',
          fontSize: '0.875rem',
          marginTop: '4px'
        },
        input: {
          backgroundColor: 'rgba(30, 30, 30, 0.6)',
          borderColor: 'rgba(255, 255, 255, 0.22)',
          fontSize: '1rem', // 16px
          color: '#f2f2f2',
          '&:focus': {
            borderColor: '#EA580C',
          },
          '&::placeholder': {
            fontSize: '14px',
            color: COLORS.textDimmed,
          },
        },
      }}
    />
  );
};

export default ThemedTextarea;

